'use client';

import type { DraggableProvidedDragHandleProps } from '@hello-pangea/dnd';
import { Card, CardContent } from '../ui/card';
import type { Bookmark } from '@/lib/bookmark/types';
import { DragHandle } from './drag-handle';
import { HandleWorkflowDropdown } from './handle-workflow-dropdown';
import Link from 'next/link';
import { useMemo } from 'react';
import { parseBookmarkQueryParams } from './utils';
import { useGetList } from '@/requests/hooks';
import { Icons } from '../icons';
import { ChevronRightIcon } from '@heroicons/react/16/solid';
import { cn } from '@/lib/cn';
import { useWorkflows } from '@/providers/workflow-provider';
import { usePathname, useSearchParams } from 'next/navigation';
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip';
import { DEFAULT_BOOKMARK_COLOR } from '@/lib/bookmark/constants';
import type { ModelName } from '@pigello/pigello-matrix';
import { getBookmarkPath } from '@/lib/bookmark/utils/bookmark-utils';
import { useCustomCountByPath } from '../../lib/bookmark/hooks/use-custom-count-by-path';

interface WorkflowBookmarkProps {
  readonly bookmark: Bookmark;
  readonly isEditMode: boolean;
  readonly workEngagementId?: string | null;
  readonly dragHandleProps?: DraggableProvidedDragHandleProps | null;
  readonly isDragging?: boolean;
  readonly variant?: 'default' | 'banner';
  readonly isLastItem?: boolean;
}

export const WorkflowBookmark = ({
  isEditMode,
  bookmark,
  workEngagementId = null,
  isDragging = false,
  dragHandleProps,
  variant = 'default',
  isLastItem = false,
}: WorkflowBookmarkProps) => {
  const {
    isActionModalOpen,
    setCurrentWorkflowFolder,
    setCurrentWorkflowBookmark,
    userWorkflows,
    workEngagementWorkflows,
  } = useWorkflows();

  const folders = useMemo(() => {
    return [
      ...(userWorkflows?.folders ?? []),
      ...(workEngagementWorkflows?.flatMap((w) => w.folders) ?? []),
    ];
  }, [userWorkflows, workEngagementWorkflows]);

  const url = getBookmarkPath(bookmark);

  return url ? (
    <Link
      href={url}
      onClick={(e) => {
        e.stopPropagation();

        if (isActionModalOpen) {
          e.preventDefault();
          return;
        }

        const folder = folders?.find((folder) =>
          folder.child_bookmarks?.some(
            (b) => b.client_id === bookmark.client_id
          )
        );

        if (folder) {
          setCurrentWorkflowFolder(folder);
        }

        setCurrentWorkflowBookmark(bookmark);
      }}
    >
      <BookmarkCard
        variant={variant}
        isEditMode={isEditMode}
        bookmark={bookmark}
        dragHandleProps={dragHandleProps}
        workEngagementId={workEngagementId}
        isDragging={isDragging}
        isLastItem={isLastItem}
      />
    </Link>
  ) : (
    <BookmarkCard
      variant={variant}
      isEditMode={isEditMode}
      bookmark={bookmark}
      dragHandleProps={dragHandleProps}
      workEngagementId={workEngagementId}
      isDragging={isDragging}
      isLastItem={isLastItem}
    />
  );
};

interface BookmarkCountProps {
  readonly bookmark: Bookmark;
  readonly variant: 'default' | 'banner';
}

const BookmarkCount = (
  {
    bookmark,
    variant,
  }: BookmarkCountProps
) => {
  const { modelName, filters, overrideUrl, overrideUrlReplaceAll, search } =
  useMemo(() => parseBookmarkQueryParams(bookmark), [bookmark]);

  const countQuery = useCustomCountByPath(bookmark.path);;

  const {
    data: countList,
    isLoading,
    isPending,
  } = useGetList({
    modelName: modelName as ModelName,
    overrideUrl,
    overrideUrlReplaceAll,
    queryParams: {
      filters: filters ?? {},
      search,
      page: 1,
      pageSize: 1,
      enableGlobalFilters: true,
    },
    enabled: !!modelName && !countQuery,
  });

  const count = countQuery?.data?.count ?? countList?.meta?.total_amount ?? '-';

  if (countQuery ? countQuery.isLoading : isLoading || isPending) {
    return <Icons.loader className='size-4 animate-spin' />;
  }

  if (variant === 'banner' && typeof count === 'number' && count > 9999) {
    return <>9999+</>;
  }

  return <>{count}</>;
};

interface BookmarkCardProps {
  readonly variant: 'default' | 'banner';
  readonly isEditMode: boolean;
  readonly bookmark: Bookmark;
  readonly dragHandleProps: DraggableProvidedDragHandleProps | null | undefined;
  readonly workEngagementId: string | null;
  readonly isDragging: boolean;
  readonly isLastItem: boolean;
}

const BookmarkCard = ({
  variant,
  isEditMode,
  bookmark,
  dragHandleProps,
  workEngagementId,
  isDragging,
  isLastItem,
}: BookmarkCardProps) => {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const url = getBookmarkPath(bookmark);

  const currentUrl = useMemo(() => {
    const queryString = searchParams.toString();
    return queryString ? `${pathname}?${queryString}` : pathname;
  }, [pathname, searchParams]);

  const isViewingBookmark = (url: string) => {
    return decodeURIComponent(url) === decodeURIComponent(currentUrl);
  };

  return (
    <div className='flex flex-row items-center'>
      <Card
        initial={false}
        className={cn(
          'flex w-[180px] cursor-pointer flex-row items-stretch gap-0 border-l-4 bg-background p-0 hover:bg-secondary',
          {
            'h-[77px] justify-center': variant === 'default',
            'h-[34px]': variant === 'banner',
            'bg-accent': isViewingBookmark(url),
            'w-[220px]': isEditMode,
          }
        )}
        style={{
          borderLeftColor: bookmark.color ?? DEFAULT_BOOKMARK_COLOR,
        }}
      >
        <DragHandle dragHandleProps={dragHandleProps} />
        <CardContent className='flex w-full items-stretch justify-between gap-4'>
          <div
            className={cn('flex min-w-0 flex-row', {
              'flex-col justify-center p-2': variant === 'default',
              'flex-row items-center gap-1.5 p-2': variant === 'banner',
            })}
          >
            {variant === 'banner' && (
              <div className='flex items-center justify-center text-[12px] font-semibold'>
                <BookmarkCount bookmark={bookmark} variant={variant} />
              </div>
            )}
            {variant === 'default' && (
              <div className='text-lg font-semibold'><BookmarkCount bookmark={bookmark} variant={variant} /></div>
            )}
            <Tooltip>
              <TooltipTrigger asChild>
                <span
                  className={cn('min-w-0', {
                    'line-clamp-2 text-xs': variant === 'default',
                    'line-clamp-1 text-[12px]': variant === 'banner',
                  })}
                >
                  {bookmark.name}
                </span>
              </TooltipTrigger>
              <TooltipContent className='max-w-[400px]'>
                {bookmark.name}
              </TooltipContent>
            </Tooltip>
          </div>
          <HandleWorkflowDropdown
            workEngagementId={workEngagementId}
            type='bookmark'
            currentItem={bookmark}
            isEditMode={isEditMode}
          />
        </CardContent>
      </Card>
      <ChevronRightIcon
        className={cn(
          'size-4 text-muted-foreground/50 transition-opacity duration-200 ease-in-out',
          isDragging || isLastItem ? 'opacity-0' : 'opacity-100'
        )}
      />
    </div>
  );
};
